import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import TaskComponent from "@/components/tasks/Task";
import {
  Task,
  TaskResponse,
  TaskPromptResponse,
  Project,
  TaskPrompt,
} from "@/api/types";
import SimplePage from "@/components/structure/SimplePage";
import { apiClient } from "@/api";
import { PromptType, TaskPromptProps } from "@/components/tasks/types";
import { useAuth } from "@/contexts/AuthContext";
import AppLinkButton from "@/components/structure/AppLinkButton";
import RoleChips from "@/components/auth/RoleChips";
import { ProjectProvider, useProject } from "@/contexts/ProjectContext";
import AppButton from "@/components/structure/AppButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AlertBox from "@/components/structure/AlertBox";

interface ReviewTaskHeaderProps {
  taskData?: Task | null;
  taskResponse?: TaskResponse | null;
}

export function ReviewTaskHeader({
  taskData,
  taskResponse,
}: ReviewTaskHeaderProps) {
  const { project } = useProject();

  return (
    <div className="mb-4 border-blue-100 border-1 rounded p-3 bg-blue-50">
      <h1 className="text-lg flex items-center">
        <strong>Project:</strong>{" "}
        <AppLinkButton
          to={`/projects/${project?.id}/`}
          variant="primary"
          className="py-1 ml-1"
        >
          <span className="mr-2">{project?.name}</span>
        </AppLinkButton>
        <RoleChips roles={project?.roles} />
      </h1>
      <h1 className="text-lg">
        <strong>Task:</strong> {taskData && `${taskData.title}`}
      </h1>
      <div className="mt-1 text-sm text-gray-500 dark:text-gray-500">
        Your response was submitted on{" "}
        {new Date(taskResponse?.createdAt!).toLocaleString()}
      </div>
    </div>
  );
}

export default function ReviewTaskResponse() {
  const { isAuthenticated } = useAuth();
  const { taskId, taskResponseId } = useParams<{
    taskId: string;
    taskResponseId: string;
  }>();
  const [taskData, setTaskData] = useState<Task | null>(null);
  const [projectData, setProjectData] = useState<Project | null>(null);
  const [taskResponse, setTaskResponse] = useState<TaskResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTask = async () => {
      try {
        const data = await apiClient.get<Task>(`/tasks/${taskId}/`);

        setTaskData(data);
        setTaskResponse(data.lastResponse || null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    if (taskId) {
      fetchTask();
    }
  }, [isAuthenticated, taskId]);

  // fetch the project data once the task data is loaded
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const data = await apiClient.get<Project>(
          `/projects/${taskData?.project}/`
        );
        setProjectData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      }
    };
    if (taskData) {
      fetchProject();
    }
  }, [taskData]);

  let message = null;
  let taskToReview = null;

  if (loading) {
    message = <div>Loading task {taskId}...</div>;
  } else if (error) {
    message = <div>Error: {error}</div>;
  } else if (!taskData) {
    message = (
      <div className="mb-4 border-red-400 bg-red-50 border-1 rounded p-4">
        <div className="text-xl font-bold">Task not found</div>
        <div className="mb-0">
          The task you are trying to review does not exist. It may have been
          deleted.
        </div>
        <AppLinkButton to={`/projects/`} variant="primary" className="mt-2">
          Go to Projects
        </AppLinkButton>
      </div>
    );
  } else if (!taskResponse) {
    message = (
      <AlertBox type="warning" headerText="No Response Submitted">
        <div className="text-xl font-bold">
          You have not yet submitted this task!
        </div>
        <div className="mb-0">
          Please return to the task page to submit your responses for review.
        </div>
        <AppButton
          href={`/tasks/${taskId}/`}
          variant="primary"
          className="mt-2"
          forwardArrow={true}
        >
          Go to Task
        </AppButton>
      </AlertBox>
    );
  } else {
    // convert prompts into TaskPromptProps format
    const prompts = taskData.prompts.map((prompt) => ({
      ...prompt,
      type: prompt.type as PromptType, // assuming type is compatible
      options: prompt.options || [], // default to empty array if no options
      value: taskResponse
        ? taskResponse.promptResponses?.find(
            (pr: TaskPromptResponse) => pr.taskPrompt === prompt.id
          )?.responseText
        : undefined,
    })) as TaskPrompt[];

    const combinedTaskData = {
      ...taskData,
      prompts: prompts,
    };

    taskToReview = (
      <TaskComponent
        id={taskId}
        task={combinedTaskData}
        project={projectData}
        readOnly={true}
        showTaskSummary={false}
        isReviewing={true}
      />
    );
  }

  return (
    <SimplePage title="Review Task Response">
      <ProjectProvider projectId={taskData?.project}>
        <ReviewTaskHeader taskData={taskData} taskResponse={taskResponse} />

        {!message && (
          <div className="mb-4 border-yellow-200 bg-yellow-50 border-1 rounded p-4">
            <div className="text-xl font-bold">Instructions:</div>
            <p className="mb-1">
              Your task submission needs a bit of elaboration.
            </p>
            <p className="mb-0">
              Below, you'll chat with an LLM that will help you refine your
              answers. Once you're satisfied, you can resubmit your task for
              review.
            </p>
          </div>
        )}

        {message && <div className="mb-4">{message}</div>}

        {/* Render the task component */}
        {taskToReview && <div>{taskToReview}</div>}
      </ProjectProvider>
    </SimplePage>
  );
}
